import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../@/components/ui/button";
import { Progress } from "../@/components/ui/progress";
import { EXAMPLE_IMAGES } from "../data/exampleImages";

interface ImageUploaderProps {
  onImageUpload: (imageUrl: string, exampleId?: string) => void;
  initialImageUrl?: string;
  /** Slim bar (button + examples) instead of the full drop target */
  compact?: boolean;
}

const FastImageUploader: React.FC<ImageUploaderProps> = ({
  onImageUpload,
  initialImageUrl,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialImageUrl ?? null,
  );
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The app owns the source URL (upload, example, or shared-link restore) —
  // mirror it into the preview so a source swap stays visible in both the
  // full dropzone and the compact bar.
  useEffect(() => {
    if (initialImageUrl) setPreviewUrl(initialImageUrl);
  }, [initialImageUrl]);

  // Stroke icons (no emoji): one camera for the empty state, one file
  // for the browse action, one refresh for re-picking
  const CameraIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-10 w-10" aria-hidden>
      <path d="M4 8.5A2.5 2.5 0 0 1 6.5 6h1.6a1 1 0 0 0 .83-.45l.74-1.1a1 1 0 0 1 .83-.45h3a1 1 0 0 1 .83.45l.74 1.1a1 1 0 0 0 .83.45h1.6A2.5 2.5 0 0 1 20 8.5v8A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5v-8Z" />
      <circle cx="12" cy="12.5" r="3.25" />
    </svg>
  );
  const FileIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden>
      <path d="M13.5 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.5L13.5 3Z" />
      <path d="M13.5 3v5.5H19" />
      <path d="M12 12v5M9.5 14.5 12 12l2.5 2.5" />
    </svg>
  );
  const RefreshIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden>
      <path d="M20 11.5A8 8 0 0 0 6.3 6.3L4 8.5" />
      <path d="M4 4.5v4h4" />
      <path d="M4 12.5a8 8 0 0 0 13.7 5.2l2.3-2.2" />
      <path d="M20 19.5v-4h-4" />
    </svg>
  );

  // Image compression utility
  const compressImageFast = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      const processImage = (img: HTMLImageElement) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        // Calculate optimal size for web (max 1200px width, maintain aspect ratio)
        const maxWidth = 1200;
        const scaleFactor = img.width > maxWidth ? maxWidth / img.width : 1;
        const width = Math.round(img.width * scaleFactor);
        const height = img.height * scaleFactor;

        canvas.width = width;
        canvas.height = height;

        // Apply subtle sharpening and brightness adjustment
        ctx.filter = "contrast(1.1) brightness(1.05) saturate(1.1)";
        ctx.drawImage(img, 0, 0, width, height);

        // Compress as JPEG with optimized quality
        const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.85);
        resolve(compressedDataUrl);
      };

      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        const img = new Image();
        img.onload = () => processImage(img);
        img.src = dataUrl;
      };

      reader.readAsDataURL(file);
    });
  }, []);

  // Optimized drag handling
  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);

      const files = event.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file && file.type.startsWith("image/")) {
          setIsProcessing(true);
          setUploadProgress(10);
          setPreviewUrl(null);

          // Fast compress and get preview
          const compressedUrl = await compressImageFast(file);
          setPreviewUrl(compressedUrl);
          setUploadProgress(90);
          onImageUpload(compressedUrl);

          setUploadProgress(100);
          setTimeout(() => {
            setIsProcessing(false);
            setUploadProgress(0);
          }, 500);
        }
      }
    },
    [compressImageFast, onImageUpload],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(true);
    },
    [],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
    },
    [],
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(true);
    },
    [],
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file && file.type.startsWith("image/")) {
          setIsProcessing(true);
          setUploadProgress(10);
          setPreviewUrl(null);

          const compressedUrl = await compressImageFast(file);
          setPreviewUrl(compressedUrl);
          setUploadProgress(90);
          onImageUpload(compressedUrl);

          setUploadProgress(100);
          setTimeout(() => {
            setIsProcessing(false);
            setUploadProgress(0);
          }, 500);
        }
      }
    },
    [compressImageFast, onImageUpload],
  );

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.dataTransfer.effectAllowed = "copy";
      event.dataTransfer.setData("text/plain", "");
    },
    [],
  );

  const openFilePicker = useCallback(() => {
    if (!isProcessing) fileInputRef.current?.click();
  }, [isProcessing]);

  const loadExample = useCallback(
    (example: { id: string; path: string }) => {
      if (isProcessing) return;
      const url = `${window.location.origin}${example.path}`;
      // Show the example in the dropzone preview so the click has visible
      // feedback (App starts the worker conversion via onImageUpload)
      setPreviewUrl(url);
      onImageUpload(url, example.id);
    },
    [isProcessing, onImageUpload],
  );

  const onDropZoneKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (previewUrl || isProcessing) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openFilePicker();
      }
    },
    [previewUrl, isProcessing, openFilePicker],
  );

  const dropZoneLabel = `${t("uploader.title")}. ${t("uploader.dragDrop")}`;

  // Example-image picker — the filmstrip: each chip shows the actual
  // painting it loads (decorative img; the accessible name stays the
  // example's label)
  const examples = (<>
    {!isProcessing && (
      <div className="mt-5">
        <span className="eyebrow" id="example-picker-label">
          {t("uploader.tryExample")}
        </span>
        <div
          className="mt-2 flex flex-wrap gap-2"
          role="group"
          aria-labelledby="example-picker-label"
        >
          {EXAMPLE_IMAGES.map((ex, index) => (
            <button
              key={ex.id}
              type="button"
              title={ex.label}
              onClick={() => loadExample(ex)}
              className="flex items-center gap-2 rounded-full border-[1.5px] border-ink bg-surface py-1 pl-1 pr-3 text-sm font-semibold text-ink shadow-hard-sm transition duration-150 ease-out-expo motion-reduce:transition-none hover:text-brick hover:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick active:translate-x-0.5 active:translate-y-0.5 active:shadow-hard-press"
            >
              <img
                src={`${window.location.origin}${ex.path}`}
                alt=""
                aria-hidden
                className="h-7 w-7 rounded-full object-cover border border-ink/20 shrink-0"
                loading={index === 0 ? "eager" : "lazy"}
                fetchPriority={index === 0 ? "high" : "auto"}
              />
              <span>{ex.label}</span>
            </button>
          ))}
        </div>
      </div>
    )}
  </>);

  // Compact bar (used under a filled stage): the current source, a file
  // picker and the example filmstrip — swap the input without leaving
  // the stage.
  if (compact) {
    return (
      <div className="relative w-full">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          {previewUrl && (
            <img
              src={previewUrl}
              alt={t("uploader.previewAlt")}
              className="h-14 w-14 shrink-0 rounded-xl object-cover border border-ink/20 shadow-hard-sm"
              loading="eager"
              crossOrigin="anonymous"
            />
          )}
          <Button
            onClick={openFilePicker}
            disabled={isProcessing}
            className="btn-press gap-2"
          >
            <FileIcon />
            <span>{t("uploader.chooseFile")}</span>
          </Button>
        </div>
        {/* The examples are the quick swap, here and in the empty state */}
        {examples}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <div
        tabIndex={previewUrl || isProcessing ? -1 : 0}
        aria-label={dropZoneLabel}
        aria-busy={isProcessing}
        className={`
          upload-dropzone relative
          w-full p-6 sm:p-8
          bg-surface border-2 border-ink/90 rounded-2xl
          transition duration-200 ease-out-expo cursor-pointer motion-reduce:transition-none
          focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick
          ${
            isDragging
              ? "bg-signal-soft/70 border-brick translate-x-[-2px] translate-y-[-2px] shadow-hard-drag"
              : "shadow-hard hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-hard-hover active:translate-x-[2px] active:translate-y-[2px] active:shadow-hard-press"
          }
        `}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDragStart={handleDragStart}
        onClick={openFilePicker}
        onKeyDown={onDropZoneKeyDown}
      >
        <div className="studs-row w-28 opacity-70 mb-6" aria-hidden />

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />

        {previewUrl ? (
          <div className="relative mt-4 group">
            <img
              src={previewUrl}
              alt={t("uploader.previewAlt")}
              className="w-full h-52 object-cover rounded-lg shadow-lg"
              loading="eager"
              crossOrigin="anonymous"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 rounded-lg" />
            <Button
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              disabled={isProcessing}
              variant="secondary"
              className="absolute bottom-3 right-3 gap-2 btn-press"
            >
              <RefreshIcon />
              <span>{t("uploader.chooseFile")}</span>
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 space-y-3">
            {isProcessing ? (
              <div className="text-center">
                <div className="relative inline-block">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-canvas border-t-brick [prefers-reduced-motion:reduce]:animate-none"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-semibold text-brick">{uploadProgress}%</span>
                  </div>
                </div>
                <p className="text-ink-muted mt-3 font-semibold">
                  {t("uploader.processing")}
                </p>
              </div>
            ) : (
              <>
                <div className="text-ink-muted mb-1" aria-hidden>
                  <CameraIcon />
                </div>
                <p className="text-ink font-display font-extrabold text-xl sm:text-2xl tracking-tight max-w-md text-center leading-snug">
                  {t("uploader.dragDrop")}
                </p>
                <Button
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  className="btn-press gap-2 mt-2"
                >
                  <FileIcon />
                  <span>{t("uploader.chooseFile")}</span>
                </Button>
              </>
            )}
          </div>
        )}

        {/* Upload progress bar */}
        {isProcessing && uploadProgress > 0 && uploadProgress < 100 && (
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-xs text-ink-muted font-semibold">
              <span>{t("uploader.processing")}</span>
              <span>{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="h-2.5" />
          </div>
        )}
      </div>

      {examples}
    </div>
  );
};

export default FastImageUploader;
